import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Html5Qrcode } from 'html5-qrcode';
import { previewFollowupScan, saveFollowupScan } from '../../services/api';
import { FiCamera, FiCheck, FiX, FiRefreshCw } from 'react-icons/fi';

const ScanFollowup = () => {
  const { t } = useTranslation();
  const scannerInstance = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [token, setToken] = useState(null);
  const [member, setMember] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  const stop = async () => {
    try { if (scannerInstance.current?.isScanning) await scannerInstance.current.stop(); } catch {}
    try { await scannerInstance.current?.clear(); } catch {}
    scannerInstance.current = null;
    setScanning(false);
  };

  const start = async () => {
    setError(null);
    setMember(null);
    setToken(null);
    setSaved(null);
    setNotes('');
    setScanning(true);

    setTimeout(async () => {
      try {
        scannerInstance.current = new Html5Qrcode('qr-reader');
        await scannerInstance.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          async (decoded) => {
            await stop();
            setToken(decoded);
            try {
              const r = await previewFollowupScan(decoded);
              setMember(r.data);
            } catch (e) {
              setError(e.response?.data?.error || t('scanFollowup.failedVerify'));
            }
          },
          () => {} // ignore per-frame errors
        );
      } catch (e) {
        setError(t('markAttendance.cameraDenied'));
        setScanning(false);
      }
    }, 100);
  };

  useEffect(() => () => { stop(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const r = await saveFollowupScan(token, notes.trim() || null);
      setSaved(r.data);
      setMember(null);
      setToken(null);
      setNotes('');
    } catch (e) {
      setError(e.response?.data?.error || t('scanFollowup.failedSave'));
    } finally { setBusy(false); }
  };

  return (
    <div className="page-content">
      <div className="page-header"><div><h1>{t('scanFollowup.title')}</h1><p>{t('scanFollowup.description')}</p></div></div>

      {!scanning && !member && !saved && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📱</div>
            <h3 style={{ marginBottom: 8 }}>{t('markAttendance.readyToScan')}</h3>
            <p style={{ color: '#6b7280', marginBottom: 20 }}>{t('scanFollowup.readyDesc')}</p>
            <button className="btn btn-primary" onClick={start}><FiCamera /> {t('markAttendance.startCamera')}</button>
            {error && <div style={{ color: '#dc2626', marginTop: 16, fontSize: 13 }}>{error}</div>}
          </div>
        </div>
      )}

      {scanning && (
        <div className="card">
          <div className="card-body">
            <div id="qr-reader" style={{ width: '100%', maxWidth: 400, margin: '0 auto' }} />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={stop}><FiX /> {t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {member && !saved && (
        <div className="card">
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>
                <FiCheck />
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{t('scanFollowup.qrVerified')}</div>
                <h2 style={{ margin: 0, fontSize: 22 }}>{member.name}</h2>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {member.card_number && <span style={{ fontFamily: 'monospace', marginRight: 12 }}>{member.card_number}</span>}
                  {member.phone && <span>{member.phone}</span>}
                </div>
                {member.last_followup && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{t('scanFollowup.lastFollowup', { date: member.last_followup })}</div>}
              </div>
            </div>

            <div className="form-group">
              <label>{t('scanFollowup.notesOptional')}</label>
              <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('scanFollowup.notesPlaceholder')} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? t('common.saving') : t('scanFollowup.saveVisit')}</button>
              <button className="btn btn-secondary" onClick={() => { setMember(null); setToken(null); setNotes(''); }}>{t('common.cancel')}</button>
            </div>
            {error && <div style={{ color: '#dc2626', marginTop: 16, fontSize: 13 }}>{error}</div>}
          </div>
        </div>
      )}

      {saved && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: 56, color: '#16a34a', marginBottom: 12 }}>✓</div>
            <h2 style={{ marginBottom: 8 }}>{t('scanFollowup.visitRecorded')}</h2>
            <p style={{ color: '#6b7280', marginBottom: 20 }}>{t('scanFollowup.visitSavedFor', { name: saved.member?.name })}</p>
            <button className="btn btn-primary" onClick={start}><FiRefreshCw /> {t('scanFollowup.scanAnother')}</button>
          </div>
        </div>
      )}

      {error && !scanning && !member && !saved && (
        <div style={{ color: '#dc2626', marginTop: 16, fontSize: 13, textAlign: 'center' }}>{error}</div>
      )}
    </div>
  );
};

export default ScanFollowup;
